import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';

/**
 * Ilustraciones propias de la consola (nada de stock ni emoji). Un solo idioma visual: láminas en
 * `card` con filo `border`, y las pistas y nodos de circuito del isotipo "M" de Metamindia en el
 * color de señal (`highlight`: cian profundo en claro, cian en oscuro). Todo con tokens, así que
 * funcionan igual en los dos temas. Son decorativas (`aria-hidden`): el texto de al lado dice lo
 * que pasa.
 */

type SvgProps = Omit<ComponentProps<'svg'>, 'children' | 'viewBox'>;

function Art({
  viewBox,
  className,
  children,
  ...props
}: SvgProps & { viewBox: string; children: ReactNode }) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      aria-hidden
      focusable="false"
      className={cn('h-28 w-auto shrink-0 text-highlight', className)}
      {...props}
    >
      {children}
    </svg>
  );
}

/** Pista de circuito (trazo fino en el color de señal) con un nodo en la punta. */
function Trace({ d, node }: { d: string; node: [number, number] }) {
  return (
    <>
      <path
        d={d}
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={node[0]} cy={node[1]} r="3" fill="currentColor" fillOpacity="0.7" />
    </>
  );
}

function Ground({ cx = 100, cy = 124, rx = 66 }: { cx?: number; cy?: number; rx?: number }) {
  return <ellipse cx={cx} cy={cy} rx={rx} ry="6" className="fill-muted-foreground/10" />;
}

/** Bandeja al día: una carta que entra y el tilde verde. Para "no hay conversaciones por responder". */
export function InboxIllustration(props: SvgProps) {
  return (
    <Art viewBox="0 0 200 136" {...props}>
      <Ground />
      <Trace d="M8 98h24l10-10h14" node={[8, 98]} />
      <Trace d="M144 98h22l10 10h16" node={[192, 108]} />
      <g transform="rotate(-9 104 34)">
        <rect
          x="84"
          y="18"
          width="40"
          height="28"
          rx="4"
          className="fill-card stroke-muted-foreground/40"
        />
        <path
          d="m85 20 19 14 19-14"
          className="stroke-muted-foreground/45"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </g>
      <path
        d="M56 78 70 52h60l14 26v28a8 8 0 0 1-8 8H64a8 8 0 0 1-8-8Z"
        className="fill-card stroke-muted-foreground/40"
        strokeWidth="1.5"
      />
      <path
        d="M56 78h24a6 6 0 0 1 6 6v1a6 6 0 0 0 6 6h16a6 6 0 0 0 6-6v-1a6 6 0 0 1 6-6h24"
        className="stroke-muted-foreground/40"
        strokeWidth="1.5"
      />
      <path
        d="M78 60h44"
        className="stroke-muted-foreground/20"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="142" cy="50" r="13" className="fill-card stroke-success" strokeWidth="1.5" />
      <path
        d="m136 50 4.5 4.5L149 46"
        className="stroke-success"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Art>
  );
}

/** Tienda con lupa: todavía no hay tiendas encontradas (o no con esos filtros). */
export function LeadsIllustration(props: SvgProps) {
  const scallops = Array.from({ length: 5 }, () => 'q8.4 9 16.8 0').join(' ');
  return (
    <Art viewBox="0 0 200 136" {...props}>
      <Ground cx={96} />
      <Trace d="M58 100H36l-8 8H10" node={[10, 108]} />
      <Trace d="M150 64V44l8-8h28" node={[190, 36]} />
      <rect
        x="58"
        y="58"
        width="72"
        height="56"
        rx="4"
        className="fill-card stroke-muted-foreground/40"
        strokeWidth="1.5"
      />
      <path
        d="M52 58 60 38h68l8 20Z"
        className="fill-highlight/10 stroke-highlight/60"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M80 38 76 58M94 38v20M108 38l4 20"
        className="stroke-highlight/40"
        strokeWidth="1.5"
      />
      <path d={`M52 58 ${scallops}`} className="fill-card stroke-highlight/60" strokeWidth="1.5" />
      <rect
        x="70"
        y="80"
        width="18"
        height="34"
        rx="2"
        className="fill-muted stroke-muted-foreground/40"
      />
      <rect
        x="96"
        y="76"
        width="24"
        height="18"
        rx="2"
        className="fill-muted stroke-muted-foreground/40"
      />
      <circle cx="85" cy="98" r="1.5" className="fill-muted-foreground/60" />
      <circle
        cx="150"
        cy="84"
        r="16"
        className="fill-card/80 stroke-muted-foreground/70"
        strokeWidth="3"
      />
      <path
        d="m161.5 95.5 12 12"
        className="stroke-muted-foreground/70"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M143 80a8 8 0 0 1 8-6"
        className="stroke-highlight/70"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Art>
  );
}

/** Monitor con pulso plano: todo en calma, sin alertas. */
export function AlertsIllustration(props: SvgProps) {
  return (
    <Art viewBox="0 0 200 136" {...props}>
      <Ground />
      <Trace d="M40 90H22l-8 8H6" node={[6, 98]} />
      <Trace d="M160 62h16l8-8h10" node={[194, 54]} />
      <rect
        x="40"
        y="34"
        width="120"
        height="76"
        rx="10"
        className="fill-card stroke-muted-foreground/40"
        strokeWidth="1.5"
      />
      <path d="M40 50h120" className="stroke-muted-foreground/40" strokeWidth="1.5" />
      <circle cx="50" cy="42" r="2" className="fill-muted-foreground/30" />
      <circle cx="58" cy="42" r="2" className="fill-muted-foreground/30" />
      <circle cx="66" cy="42" r="2" className="fill-muted-foreground/30" />
      <path
        d="M52 94h96"
        className="stroke-muted-foreground/15"
        strokeWidth="1.5"
        strokeDasharray="3 4"
      />
      <path
        d="M52 78h32l5-10 7 20 5-10h47"
        className="stroke-success"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="148" cy="78" r="3.5" className="fill-success" />
      <circle cx="148" cy="78" r="7" className="stroke-success/40" strokeWidth="1.5" />
    </Art>
  );
}

/** "404" dibujado con pistas de circuito, con el cero cortado: la ruta no lleva a ningún lado. */
export function NotFoundIllustration(props: SvgProps) {
  return (
    <Art viewBox="0 0 240 124" {...props}>
      <Ground cx={120} cy={116} rx={96} />
      <g stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M34 18v46h40M62 18v82" />
        <path d="M124 18h-6a22 22 0 0 0-22 22v38a22 22 0 0 0 22 22h8a22 22 0 0 0 22-22V54" />
        <path d="M168 18v46h40M196 18v82" />
      </g>
      <g className="fill-card" stroke="currentColor" strokeWidth="2">
        <circle cx="34" cy="18" r="4.5" />
        <circle cx="62" cy="100" r="4.5" />
        <circle cx="168" cy="18" r="4.5" />
        <circle cx="196" cy="100" r="4.5" />
      </g>
      <circle cx="124" cy="18" r="4.5" className="fill-card stroke-warning" strokeWidth="2" />
      <circle cx="148" cy="54" r="4.5" className="fill-card stroke-warning" strokeWidth="2" />
      <path
        d="m134 28 4 3m3 7 1 5m-11-12 1-4"
        className="stroke-warning"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Art>
  );
}

/** Enchufe desconectado: algo falló entre la pantalla y el servidor. */
export function ErrorIllustration(props: SvgProps) {
  return (
    <Art viewBox="0 0 220 124" {...props}>
      <Ground cx={110} cy={112} rx={84} />
      <path
        d="M8 66h60"
        className="stroke-muted-foreground/50"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <rect
        x="68"
        y="52"
        width="30"
        height="28"
        rx="6"
        className="fill-card stroke-muted-foreground/40"
        strokeWidth="1.5"
      />
      <path
        d="M98 60h12M98 72h12"
        className="stroke-muted-foreground/70"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <rect
        x="136"
        y="46"
        width="32"
        height="40"
        rx="7"
        className="fill-card stroke-muted-foreground/40"
        strokeWidth="1.5"
      />
      <rect x="144" y="58" width="4" height="7" rx="1.5" className="fill-muted-foreground/50" />
      <rect x="156" y="58" width="4" height="7" rx="1.5" className="fill-muted-foreground/50" />
      <path
        d="M168 66h44"
        className="stroke-muted-foreground/50"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="m118 50 5 6-4 3 6 7M126 82l-2-6 5-1-3-6"
        className="stroke-destructive"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="66" r="3" className="fill-highlight/70" />
      <circle cx="212" cy="66" r="3" className="fill-highlight/70" />
    </Art>
  );
}

/**
 * Héroe del login: la "M" de Metamindia como bus de circuito, con tres láminas conectadas a sus
 * nodos: los envíos (barras), una conversación (burbujas) y una tienda encontrada (con su nota).
 * Vive sobre el panel azul noche en los dos temas, así que usa los colores de marca directos.
 */
export function BrandHero({ className, ...props }: SvgProps) {
  return (
    <svg
      viewBox="0 0 480 340"
      fill="none"
      aria-hidden
      focusable="false"
      className={cn('h-auto w-full', className)}
      {...props}
    >
      <defs>
        <radialGradient id="mm-hero-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#00e5ff" stopOpacity="0.28" />
          <stop offset="1" stopColor="#00e5ff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="196" cy="180" r="150" fill="url(#mm-hero-glow)" />

      {/* La M: tres pistas paralelas (bus), como el isotipo. */}
      <g stroke="#00e5ff" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M150 270V120l46 52 46-52v150" strokeWidth="3" />
        <path d="M168 270V168l28 32 28-32v102" strokeWidth="1.5" strokeOpacity="0.55" />
        <path d="M132 270V96l64 72 64-72v174" strokeWidth="1.5" strokeOpacity="0.35" />
      </g>
      {/* Pistas hacia las láminas. */}
      <g
        stroke="#ffffff"
        strokeOpacity="0.35"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M132 96h-28l-10-10v-10" />
        <path d="M150 270v22H44" />
        <path d="M242 270v22h70" />
        <path d="M260 96V70l20-20h64" />
        <path d="M260 230h40l18 18h40" />
      </g>
      <g fill="#0a2342" stroke="#00e5ff" strokeWidth="2">
        <circle cx="132" cy="96" r="5" />
        <circle cx="260" cy="96" r="5" />
        <circle cx="196" cy="172" r="6" />
        <circle cx="150" cy="270" r="5" />
        <circle cx="242" cy="270" r="5" />
        <circle cx="260" cy="230" r="5" />
        <circle cx="44" cy="292" r="4" />
        <circle cx="312" cy="292" r="4" />
      </g>
      <circle cx="196" cy="172" r="2.5" fill="#00e5ff" />

      {/* Lámina 1: envíos por día. */}
      <g transform="translate(20 4)">
        <rect
          width="118"
          height="72"
          rx="12"
          fill="#ffffff"
          fillOpacity="0.07"
          stroke="#ffffff"
          strokeOpacity="0.16"
        />
        <rect x="14" y="14" width="42" height="6" rx="3" fill="#ffffff" fillOpacity="0.45" />
        <g fill="#00e5ff">
          <rect x="14" y="44" width="10" height="14" rx="2" fillOpacity="0.45" />
          <rect x="30" y="36" width="10" height="22" rx="2" fillOpacity="0.6" />
          <rect x="46" y="40" width="10" height="18" rx="2" fillOpacity="0.45" />
          <rect x="62" y="30" width="10" height="28" rx="2" fillOpacity="0.75" />
          <rect x="78" y="26" width="10" height="32" rx="2" />
        </g>
        <rect x="94" y="34" width="10" height="24" rx="2" fill="#61ce70" />
      </g>

      {/* Lámina 2: una conversación. */}
      <g transform="translate(344 18)">
        <rect
          width="124"
          height="92"
          rx="12"
          fill="#ffffff"
          fillOpacity="0.07"
          stroke="#ffffff"
          strokeOpacity="0.16"
        />
        <path
          d="M14 18h70a8 8 0 0 1 8 8v10a8 8 0 0 1-8 8H24l-10 8V26a8 8 0 0 1 0-8Z"
          fill="#ffffff"
          fillOpacity="0.14"
        />
        <rect x="24" y="26" width="52" height="5" rx="2.5" fill="#ffffff" fillOpacity="0.55" />
        <rect x="24" y="35" width="34" height="5" rx="2.5" fill="#ffffff" fillOpacity="0.35" />
        <path
          d="M110 56H48a8 8 0 0 0-8 8v8a8 8 0 0 0 8 8h52l10 6V64a8 8 0 0 0 0-8Z"
          fill="#00e5ff"
          fillOpacity="0.22"
        />
        <rect x="50" y="63" width="46" height="5" rx="2.5" fill="#00e5ff" fillOpacity="0.9" />
        <rect x="50" y="72" width="28" height="4" rx="2" fill="#00e5ff" fillOpacity="0.55" />
      </g>

      {/* Lámina 3: una tienda encontrada, con su puntuación. */}
      <g transform="translate(358 212)">
        <rect
          width="110"
          height="72"
          rx="12"
          fill="#ffffff"
          fillOpacity="0.07"
          stroke="#ffffff"
          strokeOpacity="0.16"
        />
        <rect x="14" y="14" width="24" height="24" rx="6" fill="#ffffff" />
        <path
          d="M20 24l2-5h8l2 5M20 24h12v8H20Z"
          stroke="#0a2342"
          strokeWidth="1.6"
          strokeLinejoin="round"
          fill="none"
        />
        <rect x="46" y="16" width="44" height="6" rx="3" fill="#ffffff" fillOpacity="0.55" />
        <rect x="46" y="28" width="28" height="5" rx="2.5" fill="#ffffff" fillOpacity="0.3" />
        <rect x="14" y="48" width="40" height="14" rx="7" fill="#61ce70" fillOpacity="0.22" />
        <circle cx="24" cy="55" r="3" fill="#61ce70" />
        <rect x="31" y="53" width="16" height="4" rx="2" fill="#61ce70" />
      </g>
    </svg>
  );
}
